<template>
  <stepper-page-template
      v-model="step"
      :steps="steps"
      :groups="stepGroups"
    >
    <template #toolbar-items>
      <v-toolbar-title class="with-divider">
        {{ title }}
      </v-toolbar-title>
    </template>

    <trade-contract-info
        :trade-contract="tradeContract"
        :companion="tradeContract.isPublishMode ? null : tradeContract.companions"
        @changeTradeContract="changeTradeContract"
        :readonly="!editable || tradeContract.status !== 'preparing'">
    </trade-contract-info>

    <router-view
        :trade-contracts="tradeContracts"
        :completable="completable"
        :errors="errors"
        :editable="editable"
        :steps="steps"
        :step="step"
        @transit="transit"
        @next-step="step++"
        @prev-step="step--">
    </router-view>
  </stepper-page-template>
</template>

<script>
import TradeContractInfo from "@/components/organisms/TradeContractInfo.vue";
import IoablePageMixin from "@/router/IoablePageMixin";
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {createTradeContractView, emptyTradeContractView} from "@/components/pages/contract/TradeContract/TradeContractView";
import StepperPageTemplate from "@/components/templates/StepperPageTemplate.vue";
import {safeMeta} from "@/router/Meta";
import TradeContractUtils from "@/components/pages/contract/TradeContract/TradeContractUtils";
import {loadAllContractProfiles} from "@/components/pages/contract/TradeContract/TradeContractBaseView";

export default {
  name: "DraftContract",
  components: {StepperPageTemplate, TradeContractInfo},
  mixins: [IoablePageMixin.extend({
    beforeRouteUpdate(to, from, next) {
      next(); // 子ページの切り替わりで再読込しない
    },
  }), TradeContractUtils],
  props: {
    tradeContractId: {
      type: String,
      required: true
    }
  },
  data() {
    const first = emptyTradeContractView();
    return {
      step: 0,
      loading: true,
      /** 先頭の取引契約 */
      tradeContract: first,
      /** すべての取引契約 */
      tradeContracts: [first],
    };
  },
  created() {
    this.$watch(
      () => this.tradeContracts.some(tc => tc.dirty),
      dirty => this.dirty = dirty
    )
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    steps() {
      const routeBase = this.$route.name.replace(/^(\/[^/]+).*$/, '$1');
      const steps = this.tradeContracts.flatMap((tc, index) => {
        const errors = tc.errors('draft');
        return [{
          id: 'dataProfile',
          label: 'データ',
          name: `${routeBase}/dataProfile`,
          params: {tradeContractIndex: index + 1},
          complete: !errors.containsCategory('dataProfile'),
          editable: true,
        }, {
          id: 'serviceProfile',
          label: 'サービス',
          name: `${routeBase}/serviceProfile`,
          params: {tradeContractIndex: index + 1},
          complete: !errors.containsCategory('serviceProfile'),
          editable: true,
        }, {
          id: 'contractProfile',
          label: '契約内容',
          name: `${routeBase}/contractProfile`,
          params: {tradeContractIndex: index + 1},
          complete: !errors.containsCategory('contractProfile'),
          editable: true,
        }];
      });
      const errors = this.tradeContract.errors('draft');
      steps.push({
        id: 'complete',
        label: '送信設定',
        name: `${routeBase}/complete`,
        complete: !errors.containsCategory('complete'),
        editable: false,
      });
      return steps;
    },
    stepGroups() {
      /**
       * 名前を生成します
       * @param tc {TradeContractView} 取引契約
       * @return {null|string} 名前
       */
      const tcName = tc => {
        if(tc.amiProposer) {
          if(tc.isSelfConsume) {
            return tc.tradeContractSource.consumerDataComponentModelName;
          } else if(tc.isSelfProduce) {
            return tc.tradeContractSource.producerDataComponentModelName;
          }
        } else {
          if(tc.isOtherConsume) {
            return tc.tradeContractSource.consumerDataComponentModelName;
          } else if(tc.isOtherProduce) {
            return tc.tradeContractSource.producerDataComponentModelName;
          }
        }
        return null;
      };
      return this.tradeContracts.map(tc => ({
        name: tcName(tc),
        stepLength: 3,
      }));
    },
    /** 編集可能かどうか */
    editable() {
      const tradeContract = this.tradeContract;
      return (tradeContract.amiIssuer && ['preparing','revising'].includes(tradeContract.status)) ||
          (tradeContract.isSubscribeMode && tradeContract.amiRecipient && tradeContract.status === 'applying') ||
          (tradeContract.isRequestOfferMode && tradeContract.amiRecipient && tradeContract.status === 'requesting');
    },
    /** 完了可能かどうか */
    completable() {
      for(const step of this.steps) {
        if(!step.complete) {
          return false;
        }
      }
      return true;
    },
    /** 送信設定ステップに表示するエラー */
    errors() {
      const errors = [];
      for(const step of this.steps) {
        if(!step.complete && step.id !== 'complete') {
          errors.push({
            level: 'warn',
            message: [
                this.$createElement('router-link', {
                  props: {
                    to: {
                      name: step.name,
                      params: step.params,
                    }
                  }
                }, [`${step.label}ステップ`]),
                'が未完了です。'
            ]
          });
        }
      }
      return errors;
    },
    /** タイトル */
    title() {
      return safeMeta(this.$route).label;
    },
    /** 戻るボタンの戻り先 */
    backButtonTo() {
      return this.backToLocation(this.tradeContract);
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
    }),
    ...mapActions({
      loadContractProfiles: 'hcs/contractProfile/findList',
    }),
    async load() {
      const {data: tradeContracts} = await this.hcsClient.get(`/trade_contracts/${this.tradeContractId}/descendants`);
      this.tradeContracts = tradeContracts.map(tc => createTradeContractView(tc, this.$store));
      this.tradeContract = this.tradeContracts[0];
      loadAllContractProfiles(this.tradeContracts);
      this.loading = false;
    },
    save() {
      return this.transit('save');
    },
    async transit(requestType) {
      const tradeContract = this.tradeContract;
      const statusText = requestType === 'save' ? '保存' : '送信';

      this.showSnackbar({
        message: `契約 ${tradeContract.name} の${statusText}中...`,
        timeout: false,
        loading: true
      });

      const loading = this.$loading({
        lock: true,
        spinner: 'el-icon-loading',
      });

      try {
        // 取引契約のステータスを遷移
        const length = this.tradeContracts.length;
        const results = new Array(length);
        for(let i=length-1; i>=0; i--) {
          results[i] = await this.tradeContracts[i].transitTradeContract(requestType);
        }

        this.showSnackbar({
          message: `契約 ${tradeContract.name} を${statusText}しました`,
        });

        // 取引契約管理画面に遷移
        this.dirty = false;
        await this.$router.push(this.statusToLocation(results[0].status));
      } catch (e) {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `契約 ${tradeContract.name} の${statusText}に失敗しました`,
        });
      } finally {
        loading.close();
      }
    },
    changeTradeContract(tradeContract) {
      this.tradeContract.name = tradeContract.name;
      this.tradeContract.description = tradeContract.description;
    },
    stepToPath(step, query = undefined) {
      const {resolved} = this.$router.resolve({
        name: step.name,
        params: step.params,
        query
      });
      return resolved.fullPath;
    },
  },
  watch: {
    '$route': {
      immediate: true,
      handler() {
        this.step = this.steps.findIndex(step => this.stepToPath(step) === this.$route.fullPath);
      }
    },
    'tradeContracts': {
      handler() {
        this.$nextTick(() => {
          this.step = this.steps.findIndex(step => this.stepToPath(step) === this.$route.fullPath);
        });
      }
    },
    step: {
      immediate: true,
      handler(step, oldStep) {
        if(step === oldStep) {
          return;
        }
        const query = this.$route.query
        if(step === -1) {
          this.$router.replace(this.stepToPath(this.steps[0], query)).catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
        } else {
          this.$router.push(this.stepToPath(this.steps[step], query)).catch(() => {}); // eslint-disable-line @typescript-eslint/no-empty-function
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
